import { Recommendation } from '@/types'
import { TrendingUp, ChevronRight, Lightbulb } from 'lucide-react'
import { cn } from '@/utils/cn'

interface RecommendationsWidgetProps {
  recommendations: Recommendation[]
  maxItems?: number
  className?: string
}

const PRIORITY_COLORS = {
  critical: 'border-l-red-500 bg-red-50 dark:bg-red-900/20',
  high: 'border-l-orange-500 bg-orange-50 dark:bg-orange-900/20',
  medium: 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
  low: 'border-l-gray-500 bg-gray-50 dark:bg-gray-900/20'
}

export function RecommendationsWidget({ 
  recommendations, 
  maxItems = 3,
  className 
}: RecommendationsWidgetProps) {
  // Filter to active recommendations and sort by priority
  const activeRecommendations = recommendations
    .filter(rec => rec.status === 'pending' || rec.status === 'in_progress')
    .sort((a, b) => {
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 }
      return priorityOrder[a.priority] - priorityOrder[b.priority]
    })
    .slice(0, maxItems)

  if (activeRecommendations.length === 0) {
    return (
      <div className={cn(
        'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6',
        className
      )}>
        <div className="flex items-center gap-3 mb-4">
          <Lightbulb className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Recommendations
          </h3>
        </div>
        
        <div className="text-center py-4">
          <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <TrendingUp className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            All recommendations completed!
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn(
      'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6',
      className
    )}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Lightbulb className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Recommendations
          </h3>
        </div>
        
        <a 
          href="/recommendations"
          className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
        >
          View All
          <ChevronRight className="w-4 h-4" />
        </a>
      </div>

      <div className="space-y-3">
        {activeRecommendations.map((recommendation) => (
          <div
            key={recommendation.id}
            className={cn(
              'border-l-4 p-3 rounded-r-lg transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50',
              PRIORITY_COLORS[recommendation.priority]
            )}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-1 truncate">
                  {recommendation.title}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                  {recommendation.description}
                </p>
                
                <div className="flex items-center gap-3 mt-2">
                  <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                    <TrendingUp className="w-3 h-3" />
                    +{recommendation.impact} pts
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                    {recommendation.priority} priority
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {recommendations.length > maxItems && (
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            +{recommendations.length - maxItems} more recommendations available
          </p>
        </div>
      )}
    </div>
  )
}